import React from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Box,
  Stack,
  IconButton,
  Divider,
  Button
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  PlayCircleOutline as PlayIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid #f0f0f0',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
    borderColor: theme.palette.primary.light,
  },
}));

const ProgramCard = ({ program, onDelete }) => {
  const formatAgeRange = (ageRange) => {
    if (!ageRange || ageRange.length === 0) return null;
    
    const ages = ageRange.map(age => age.age).sort((a, b) => a - b);
    if (ages.length === 1) return `${ages[0]} years`;
    if (ages.length === 2) return `${ages[0]}-${ages[1]} years`;
    return `${ages[0]}-${ages[ages.length - 1]} years`;
  };

  return (
    <StyledCard>
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        {/* Header with title and language chip */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: 1.3,
              flex: 1,
              mr: 2,
              color: 'text.primary'
            }}
          >
            {program.name}
          </Typography>
          <Chip
            label={program.language}
            color="primary" // Single default color for all languages
            size="small"
            sx={{ 
              fontWeight: 600,
              borderRadius: 1.5,
              height: 24,
              fontSize: '0.75rem'
            }}
          />
        </Box>

        {/* Program description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '2.8rem',
            fontSize: '0.85rem'
          }}
        >
          {program.programDetail}
        </Typography>

        {/* Program details */}
        <Stack spacing={1}>
          {program.tempName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <DescriptionIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                <strong>Template:</strong> {program.tempName}
              </Typography>
            </Box>
          )}

          {formatAgeRange(program.ageRange) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <PeopleIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                <strong>Ages:</strong> {formatAgeRange(program.ageRange)}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Video link */}
        {program.url && (
          <Box sx={{ mt: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlayIcon />}
              href={program.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5,
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                  borderColor: 'primary.main'
                }
              }}
            >
              View Video
            </Button>
          </Box>
        )}
      </CardContent>

      <Divider sx={{ mx: 2 }} />

      <CardActions
        sx={{
          p: 1.5,
          pt: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}
      >
        <IconButton
          component={Link}
          to={`/programs/edit/${program._id}`}
          size="small"
          sx={{
            mr: 1,
            width: 32,
            height: 32,
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        
        <IconButton
          onClick={() => onDelete(program)}
          size="small"
          sx={{
            width: 32,
            height: 32,
            backgroundColor: 'error.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'error.dark',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </StyledCard>
  );
};

export default ProgramCard;
